import React, { useState } from "react";
import {
  Autocomplete,
  TextField,
  MenuItem,
  ListItemIcon,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { UsersThree } from "@phosphor-icons/react";
import { useTheme } from "@mui/material/styles";
import useDebounce from "hooks/utils/useDebounce";
import { useSearchClients } from "hooks/useSearchClients";

export default function ClientSearchBar({
  userId,
  placeholder = "Search Clients",
  label,
  endAdornment,
  onSelectMemberId,
}) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);

  const debouncedInput = useDebounce(inputValue, 300);

  const searchClientsInput = {
    userId: userId,
    inputValue: debouncedInput,
    activeOnly: true,
    isPrimaryOnly: true,
    includeTeam: true,
    exactMatch: false,
    onlyAccessibleClients: true,
  };

  const { data: clientsResponse } = useSearchClients(
    userId,
    searchClientsInput.inputValue,
    searchClientsInput.activeOnly,
    searchClientsInput.isPrimaryOnly,
    searchClientsInput.includeTeam,
    searchClientsInput.exactMatch,
    searchClientsInput.onlyAccessibleClients
  );
  const clientList = clientsResponse?.data ?? [];

  const handleTeamMemberSelect = (member) => {
    setSelectedTeamMember(member);
    onSelectMemberId?.(member.userId);
  };

  return (
    <Autocomplete
      sx={{ flexGrow: 1 }}
      forcePopupIcon={false}
      options={clientList}
      filterOptions={(x) => x}
      value={selectedTeamMember}
      onChange={(_, newValue) => setSelectedTeamMember(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={() => {
        setInputValue("");
      }}
      getOptionLabel={(option) => option?.name || option?.userDisplayName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: endAdornment ?? null,
          }}
        />
      )}
      renderOption={(props, option) =>
        option.teamMembers.length > 0 &&
        option.teamMembers.map((member) => (
          <MenuItem
            {...props}
            key={member.userId}
            onClick={(e) => {
              props.onClick?.(e);
              handleTeamMemberSelect(member);
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ pl: "10px" }}>
              <ListItemIcon>
                <UsersThree weight="duotone" color={theme.palette.action.active} />
              </ListItemIcon>
              <Box sx={{ color: "text.primary" }}>{member.name}</Box>
              <Typography variant="caption">{member.roleName}</Typography>
            </Stack>
          </MenuItem>
        ))
      }
      groupBy={(option) => option.team?.name && option.child_name}
      renderGroup={(params) =>
        params.group ? (
          <div
            key={params.key}
            style={{ paddingLeft: 20, color: theme.palette.text.secondary, fontSize: 14 }}
          >
            <Typography variant="body2">{params.group}</Typography>
            {params.children}
          </div>
        ) : (
          params.children
        )
      }
    />
  );
}
